import React from 'react';

import { classnames } from '@lumapps/classnames';
import { SelectTextField, SingleSelectTextFieldProps } from '@lumapps/combobox/components/SelectTextField';
import { useDataAttributes } from '@lumapps/data-attributes';
import { FlexBox, Orientation } from '@lumapps/lumx/react';
import { buildTimeList, getLocalFormatedTime } from '@lumapps/utils/time';
import { getMatchingTime } from '@lumapps/utils/time/getMatchingTime';
import { getMoment } from '@lumapps/utils/time/getMoment';
import { Time } from '@lumapps/utils/types/Time';

import { useTimePickerField } from '../../hooks/useTimePickerField';

import './index.scss';

const moment = getMoment();

/**
 * Defines the props of the component.
 */
export interface TimePickerFieldProps extends Partial<SingleSelectTextFieldProps<Time>> {
    /** The error message to display in the helper */
    errorMessage?: string;
    /** Whether an error occured while typing a Time */
    hasError?: boolean;
    /** Whether the component is disabled or not. */
    isDisabled?: boolean;
    /** Locale (language or region) to use. */
    locale: string;
    /** Native input name property. */
    name?: string;
    /** Currently selected time. */
    value?: Time;
    /** Currently selected time. */
    label: string;
    /** On change callback. */
    onChange(value: Time | undefined): void;
    /** Min to base timeList on */
    min?: Time | undefined;
    /** is this field required */
    isRequired?: boolean;
    /** select id */
    id?: string;
    /** custom class name for wrapper */
    className?: string;
    /** options that are before the provided time will be disabled */
    disableOptionsBefore?: Time;
    /** whether the time should be changed if selected time is disabled */
    shouldChangeWhenDisabled?: boolean;
    /** whether the clear button should be displayed, if enabled onchange can return undefined */
    hasClearButton?: boolean;
    /** Custom icon for the input */
    icon?: string;
}

const CLASSNAME = 'time-picker-field';

/**
 * Displays a Select component in order to choose a specific time.
 *
 * @family Pickers
 * @param TimePickerFieldProps
 * @returns TimePickerField
 */
export const TimePickerField: React.FC<TimePickerFieldProps> = ({
    value,
    locale,
    isDisabled,
    onChange,
    label,
    min,
    isRequired = false,
    className,
    disableOptionsBefore,
    shouldChangeWhenDisabled,
    hasClearButton = false,
    icon,
    hasError,
    errorMessage,
    ...props
}) => {
    const timeList: Time[] = React.useMemo(() => {
        return buildTimeList(min);
    }, [min]);

    const { get } = useDataAttributes(CLASSNAME);
    const { formatTimeValue } = useTimePickerField({ timeList, minTime: min || disableOptionsBefore, locale });

    const [searchInput, setSearchInput] = React.useState<string>(value ? getLocalFormatedTime(value, locale) : '');

    const getFormatedTime = React.useCallback((time: Time) => getLocalFormatedTime(time, locale), [locale]);

    const matchingTime = React.useMemo(() => getMatchingTime(moment(value), locale), [locale, value]);

    /**
     * If `disableOptionsBefore` is present, it means that all items before that time object should be disabled.
     * In order to determine that, we find the index of `disableOptionsBefore` in the list of options. We then use
     * that index in order to disable all options before it.
     */
    const enabledFromIndex = React.useMemo(() => {
        let index = 0;

        if (disableOptionsBefore) {
            const disabledBeforeMatchingTime = getMatchingTime(moment(disableOptionsBefore), locale, 'ceil');

            index = timeList.findIndex((tm) => getMatchingTime(moment(tm), locale) === disabledBeforeMatchingTime);
        }

        return index;
    }, [disableOptionsBefore, locale, timeList]);

    /**
     * when current selected time is disabled (for example, when we get back to "today" from a date in future),
     * we change it to the first enabled time to make sure no disabled option remains selected.
     */
    const avoidDisabledTimeSelection = React.useCallback(
        (callback: (time: Time) => void) => {
            const matchingIndex = timeList.findIndex((tm) => getMatchingTime(moment(tm), locale) === matchingTime);

            if (matchingIndex !== undefined && matchingIndex < enabledFromIndex) {
                callback(timeList[enabledFromIndex]);
                // make sure to also change displayed time for consistency reasons and to avoid infinite loops
                onChange(timeList[enabledFromIndex]);
            }
        },
        [enabledFromIndex, locale, matchingTime, onChange, timeList],
    );

    const onTimeChange = React.useCallback(
        (time?: Time) => {
            setSearchInput(time ? getLocalFormatedTime(time, locale) : '');

            onChange(time);
        },
        [locale, onChange],
    );

    const renderOption = React.useCallback(
        (time: Time) => {
            const timeIndex = timeList.findIndex((tm) => tm === time);
            const isDisabled = timeIndex < enabledFromIndex;

            return (
                <SelectTextField.Option
                    aria-disabled={isDisabled}
                    isDisabled={isDisabled}
                    {...get({ element: 'option' })}
                />
            );
        },
        [enabledFromIndex, get, timeList],
    );

    const onTimeBlur = () => {
        const time = formatTimeValue(searchInput);
        if (time) {
            onTimeChange(time);
        }
    };

    React.useEffect(() => {
        if (shouldChangeWhenDisabled && value) {
            avoidDisabledTimeSelection(onChange);
        }
    }, [avoidDisabledTimeSelection, onChange, shouldChangeWhenDisabled, value]);

    return (
        <FlexBox orientation={Orientation.horizontal} className={classnames(CLASSNAME, className)}>
            <SelectTextField
                {...props}
                icon={icon}
                hasClearButton={hasClearButton}
                selectionType="single"
                value={value}
                hasError={hasError}
                error={errorMessage}
                label={label}
                isRequired={isRequired}
                isDisabled={isDisabled}
                options={timeList}
                getOptionId={getFormatedTime}
                renderOption={renderOption}
                onChange={onTimeChange}
                onBlur={onTimeBlur}
                onSearch={setSearchInput}
                autoFilter
            />
        </FlexBox>
    );
};

TimePickerField.displayName = 'TimePickerField';
